/**
 * The register mirror's sync state, as a hook.
 *
 * Lifted out of the old `SyncPanel`, which was a bar in the page head holding
 * the state, the freshness line and three buttons at once. The page now shows
 * the state in one line and keeps the buttons in a menu (`DataMenu`), and both
 * of those need the same numbers - so the numbers stopped being a component's
 * private state and became this.
 *
 * One caller per page. Two components polling the same endpoint four seconds
 * apart would show two different answers on the same screen.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../../lib/api";
import { getSyncStatus, triggerSync, type ArSyncRun, type ArSyncStatus } from "../../../../lib/ariregister";

/** How often a running import is asked how far it has got. */
const POLL_MS = 4_000;

/** Which of the mirror's datasets a manual run may refresh. */
export type SyncDataset = "all" | "tax" | "reports" | "procurements";

export interface SyncState {
  status: ArSyncStatus | null;
  /** The import in flight, or null. */
  running: ArSyncStatus["running"];
  /** The most recent finished run, whatever its outcome. */
  lastRun: ArSyncRun | null;
  /** A request is on its way out - the buttons are disabled while it is. */
  busy: boolean;
  /** The last thing a manual run had to say, in the operator's language. */
  message: string | null;
  start: (dataset: SyncDataset) => Promise<void>;
}

/**
 * What a refusal is called, when the server named one.
 *
 * A lookup rather than a chain of ternaries, for the reason a chain would hide:
 * a reason the server learns to send and this file has not learned to phrase
 * lands in `failed` and reads as "the import is broken". The licence gate is
 * exactly that kind of reason - nothing is broken, somebody simply has not
 * accepted the terms yet - and a generic failure would send the operator to
 * the logs instead of to the settings screen.
 */
export interface SyncMessages {
  started: string;
  failed: string;
  /** 409 licence_not_accepted: the open-data terms are not on the record. */
  licence: string;
}

export function useSyncStatus(messages: SyncMessages): SyncState {
  const [status, setStatus] = useState<ArSyncStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(() => {
    getSyncStatus()
      .then(setStatus)
      .catch(() => {
        // A failed poll is not worth a message. The next tick asks again.
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const running = status?.running ?? null;

  // Polled only while an import is in flight. An idle page costs one request.
  useEffect(() => {
    if (running && !timer.current) {
      timer.current = setInterval(load, POLL_MS);
    } else if (!running && timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [running, load]);

  // The messages are handed in rather than translated here, so this file stays
  // free of the i18n context and can be read (and tested) as plain data flow.
  const startedMsg = messages.started;
  const failedMsg = messages.failed;
  const licenceMsg = messages.licence;

  const start = useCallback(
    async (dataset: SyncDataset) => {
      setBusy(true);
      setMessage(null);
      try {
        await triggerSync({ dataset, mode: "bulk" });
        setMessage(startedMsg);
        // The run needs a moment to register itself before the status shows it.
        setTimeout(load, 600);
      } catch (err) {
        const code = err instanceof ApiError && err.body && typeof err.body === "object"
          ? (err.body as { error?: unknown }).error
          : null;
        setMessage(code === "licence_not_accepted" ? licenceMsg : failedMsg);
      } finally {
        setBusy(false);
      }
    },
    [load, startedMsg, failedMsg, licenceMsg],
  );

  return { status, running, lastRun: status?.runs[0] ?? null, busy, message, start };
}
